import { useEffect, useState, useCallback } from "react";

import { addSeconds, isAfter } from "date-fns";
import {useLocation, useNavigate} from "react-router-dom";

import programTreeService from "./program-tree-service";
import useAnlyticsScript from "./use-analytics-script";
import usePermissions from "./use-permissions";
import userDataService from "./user-data-service";
import userAlertService from "../alerts/user-alert-service";
import useStatus from "../generic/hooks/use-status/use-status";
import useSwitch from "../generic/hooks/use-switch/use-switch";
import { logOut , buildNavigationUrl, storageKey } from "../global-layout/Layout/routing-service";
import { setLanguageCookie, determineLanguage } from "../utils/cookie-language-helper";
import { configureOidcAuth, consumePostSignInReturnUrl, ensureAuthenticated } from "../utils/oidc-auth-service";
import {windowLocationToNavigationParameters} from "../utils/query-string-utils";
import stringHasValue from "../utils/string-has-value";
import loadTranslations from "../utils/translation-loader-service/translation-loader-service";

const defaultService = new programTreeService();
const defaultUserService = userDataService();
const defaultAlertService = new userAlertService();
const emptyAlerts = () => ({ systemAlerts: [] });

const routesAreEqual = (a,b) => {
  return a?.areaId === b?.areaId
    && a?.regionId === b?.regionId
    && a?.coordinatorId === b?.coordinatorId
    && a?.programId === b?.programId
    && a?.yearId === b?.yearId
    && a?.termId === b?.termId
}

const buildLastActiveUrlFromIds = ({
  areaId,
  regionId,
  coordinatorId,
  programId,
  yearId,
  termId
} = {}) => {
  if (!areaId) {
    return null;
  }

  let url = `/areas/${areaId}`;
  const hasRegionLevel = regionId || coordinatorId || programId || yearId || termId;
  if (!hasRegionLevel) {
    return url;
  }

  url = `${url}/regions/${regionId ?? "na"}`;
  url = `${url}/coordinators/${coordinatorId ?? "na"}`;

  if (!programId) {
    return url;
  }

  url = `${url}/programs/${programId}`;
  if (!yearId) {
    return url;
  }

  url = `${url}/years/${yearId}`;
  if (!termId) {
    return url;
  }

  return `${url}/terms/${termId}`;
}

const useApp = ({
  programTreeService = defaultService,
  userService = defaultUserService,
  alertService = defaultAlertService,
  permissionsService
}) => {

  useEffect(() => {
    const signoutTime = window.localStorage.getItem(storageKey);
    if (stringHasValue(signoutTime)) {
      window.localStorage.removeItem(storageKey);
      const signoutDate = new Date(parseInt(signoutTime));
      const now = new Date();
      const thresholdAfterSignout = addSeconds(signoutDate, 30);
      if (isAfter(thresholdAfterSignout, now)) {
        window.location.reload();
        return;
      }
    }
  },[]);


  const location = useLocation();
  const routerNavigate = useNavigate();
  const [warned, warnedOn,warnedOff] = useSwitch();
  const [printMode, printOn] = useSwitch();
  const [lastActiveUrl, setLastActiveUrl] = useState();

  const [routeInfo,setRouteInfo] = useState(windowLocationToNavigationParameters(location));
  const [user,setUser] = useState();
  const [alerts,setAlerts] = useState(() => emptyAlerts());
  const [configuration,setConfiguration] = useState({});
  const {
    status: coreStatus,
    //setSavingStatus: setCoreLoaded,
    setErrorStatus: setCoreError,
    setResetStatus: setCoreLoaded,
  } = useStatus({saving:true});//we want to start right away in loading state to avoid a flicker

  // #region user and impersonation stuff
  const {
    permissions,
    requestPermissionsForNavigation,
    requestPermissions,
    clear:clearPermissions,
    getCurrentPosition
  } = usePermissions(permissionsService);

  //when we start impersonating or clear impersonating we need to call
  //this so that we reload our permissions based on the specific user
  //and we should also push to "/" to land on the last active for that user
  const onImpersonationChange = useCallback (() => {
    //clear out our requested and verified permissions as they
    //were either for the logged in user, or the user we are impersonating.
    clearPermissions();
    //we need to check can admin again as we won't hit the use effect condition
    //that checks this on first log in.
    const {applicationId = 7} = configuration;
    requestPermissions(0,[
      {
        permission:"CanAdminApplication",
        targetId:applicationId.toString()
      }
    ]);
    routerNavigate("/");
  },[clearPermissions,requestPermissions,routerNavigate, configuration]);
    // #endregion

  // #region language state work
  const [languageData,setLanguageData] = useState(() => {
    const code = determineLanguage() ?? "en";
    return {
      code,
      groups:["shared","admin", "errors"]
    }
  });
  const [strings,setStrings] = useState();

  useEffect(() => {
    if (!user) {
      return;
    }
    let isCurrent = true;
    const load = async () => {
      try {
        const loadedStrings = await loadTranslations(languageData.code,languageData.groups);
        if (isCurrent) {
          setStrings(loadedStrings);
        }
      } catch (e) {
        if (isCurrent) {
          setCoreError(e);
        }
      }
    }
    setLanguageCookie(languageData.code);
    load();
    return () => {
      isCurrent = false;
    }
  },[languageData.code,languageData.groups,user,setCoreError]);

  const changeLanguage = useCallback((lang) => {
    setLanguageData(curData => {
      if (curData.code === lang) {
        return curData;//no-op
      }
      return {...curData, code:lang};
    })
  },[]);

  const requestLanguageGroups = useCallback((groups = []) => {
    setLanguageData(currentLanguageData => {
      const {groups:currentGroups = []} = currentLanguageData ?? {};
      //find any of the newly requested groups that aren't already in our strings data
      const groupsToAdd = groups.filter(g => currentGroups.indexOf(g) < 0);
      if (groupsToAdd.length === 0) {
        return currentLanguageData;
      }
      return {
        ...currentLanguageData,
        groups:[...currentGroups, ...groupsToAdd]}
    })
  },[]);
    // #endregion

  useEffect(() => {
    let isMounted = true;
    const load = async() => {
      try {
        const config = await userService.getConfiguration();
        configureOidcAuth(config);

        const authenticated = await ensureAuthenticated();
        if (!authenticated) {
          return;
        }

        //if we just came back from the sign-in redirect, the auth service restored the
        //original url with history.replaceState — react-router never sees that, so its
        //location still says "/auth" (no areaId) and the route-info effect would fall
        //into loadLastActive and stomp the url the user actually asked for.
        //push the restored url through the same lastActiveUrl mechanism so the router
        //catches up before the user loads and navigation kicks off.
        const postSignInReturnUrl = consumePostSignInReturnUrl();
        if (stringHasValue(postSignInReturnUrl)) {
          setLastActiveUrl(postSignInReturnUrl);
        }

        alertService.getUserAlerts()
          .then((loadedAlerts) => {
            if (!isMounted) {
              return;
            }
            setAlerts(loadedAlerts ?? emptyAlerts());
          })
          .catch((alertError) => {
            if (!isMounted) {
              return;
            }
            // Alerts should not block core app load or force auth churn.
            console.warn("load user alerts failed; continuing without alerts", alertError); // eslint-disable-line no-console
            setAlerts(emptyAlerts());
          });

        const myUser = await userService.getUser();
        //Our endpoint returns a null user if the user has no permissions
        //so that we can recognize that here and boot out of the site.
        if (!myUser) {
          window.location = "https://churchofjesuschrist.org";
        }
        const {applicationId = 7} = config;
        //as soon as we know what our application id actually is, we make sure to see if we have that permission
        //note: this will
        requestPermissions(0,[
          {
            permission:"CanAdminApplication",
            targetId:applicationId.toString()
          }
        ]);

        // Set user/config first so downstream navigation load can begin immediately.
        setUser(myUser);
        setConfiguration(config);
      } catch (e) {
        if (isMounted) {
          setCoreError(e);
        }
      }
      //config is kept in session storage, so this one is basically free after first page load.
    };
    load();
    return () => {
      isMounted = false;
    };
  },[userService, alertService, requestPermissions, setCoreError]);

  // #region program tree navigation work
  useEffect(() => {
    const newRoute = windowLocationToNavigationParameters(location);
    //NOTE: we have a use effect responding to route info after our useStatus hook is declared
    //so when our location changes we update our app's route info state
    //and respond to that update by loading new navigation
    setRouteInfo(current => {
      return routesAreEqual(current,newRoute) ? current : newRoute;
    })
  },[location]);

  const [navigation, setNavigation] = useState({});

  const {analyticsPageEvent, isolatedAnalyticsPageEvent} = useAnlyticsScript({configuration,navigation});

  const {
    status: navigationStatus,
    setSavingStatus: setNavigationLoading,
    setErrorStatus: setNavigtionError,
    setResetStatus: clearNavigationStatus,
  } = useStatus({saving:true});//we want to start right away in loading state to avoid a flicker

  const guidFrom64Bit = async (num) => {
    // Convert number to string (or binary if you prefer)
    // eslint-disable-next-line no-undef
    const input = BigInt(num).toString();

    // Encode as UTF-8
    const encoder = new TextEncoder();
    const data = encoder.encode(input);

    // Hash using SHA-256
    const hashBuffer = await crypto.subtle.digest("SHA-256", data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));

    // Take first 16 bytes (128 bits)
    const hex = hashArray.slice(0, 16).map(b => b.toString(16).padStart(2, '0')).join('');

    // Format as UUID
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20, 32)}`;
  }


  //this should only ever be called when we have at least an area id in our route!
  //also not called if the route says this is a noNav route like help center or system alerts
  const loadNavigationFromRouteInfo = useCallback(async (parameterRouteInfo) => {
    let {areaId,regionId,coordinatorId,programId,yearId,termId} = parameterRouteInfo ?? {};
    setNavigationLoading();
    try {

      const loadedNavigation = await programTreeService.getNavigation(areaId,regionId,coordinatorId,programId,yearId,termId);
      if ((loadedNavigation?.areas?.length ?? 0) <= 0) {
        logOut();
        return;
      }

      if (user) {
        const {
          churchAccountId,
        } = user ?? {};

        const currentPosition = await getCurrentPosition(programId ?? coordinatorId ?? regionId ?? areaId);
        const {
          program: {
            programNumber,
            isInstitute,
            isSucceedInSchool,
            isReleaseTime,
            isStake,
            isCampusInstitute,
            isOnlineProgram
          } = {}
        } = loadedNavigation ?? {};
        // eslint-disable-next-line no-undef
        if (typeof pendo !== 'undefined') {

          let programTypeName = "";
          if (programId > 0) {
            if (isInstitute) {
              if (isCampusInstitute) {
                programTypeName = "Campus Institute";
              }
              if (isStake) {
                programTypeName = "Stake Institute";
              }
            } else if (isSucceedInSchool) {
              programTypeName = "Succeed in School";
            } else if (isReleaseTime) {
              programTypeName = "Release-time Seminary";
            } else if (isOnlineProgram) {
              programTypeName = "Online Seminary";
            } else if (isStake) {
              programTypeName = "Stake Seminary";
            }
          }

          const programName = loadedNavigation?.program?.name;
          const coordinatorName = loadedNavigation?.coordinator?.name ?? "";
          const regionName = loadedNavigation?.region?.name ?? "";
          const areaName = loadedNavigation?.area?.name ?? "";
          // eslint-disable-next-line no-undef
          pendo.initialize({
            visitor: {
              id: await guidFrom64Bit(churchAccountId),// <visitor-id-goes-here> // Required if user is logged in
              user_role: currentPosition?.title,
              program_number: programNumber,
              program_type: programTypeName,
              program_name: programName,
              coordinator_name: coordinatorName,
              region_name: regionName,
              area_name: areaName
            },

            account: {
              id: 'cea37c6a-25db-448d-70a7-9563a399628b',// Highly recommended, required if using Pendo Feedback
            }
          });
        }
      }
      clearNavigationStatus();
      requestPermissionsForNavigation(loadedNavigation);
      setNavigation(loadedNavigation);
      if (loadedNavigation.isValidAsRequested === false) {
        var url = buildNavigationUrl(loadedNavigation);//this gives us a correct url for the corrected navigation as it came back to us.
        setLastActiveUrl(url);
      }
      isolatedAnalyticsPageEvent("home", loadedNavigation);

      //if we are here then we have a verified different route, and it has loaded successfully for the user
      //so we can call to setLastActive.  That is one we don't have to wait on as a fire and forget
      programTreeService.setLastActive(areaId,regionId,coordinatorId,programId,yearId,termId);
    } catch (e) {
      setNavigtionError(e);
    }
  },[clearNavigationStatus, getCurrentPosition, isolatedAnalyticsPageEvent,
    programTreeService, requestPermissionsForNavigation, setNavigationLoading, setNavigtionError, user])


  const loadLastActive = useCallback(async () => {
    setNavigationLoading();
    try {
      const lastActive = await programTreeService.getLastActiveSetting();
      const lastActiveUrlFromSettings = buildLastActiveUrlFromIds(lastActive);
      if (stringHasValue(lastActiveUrlFromSettings)) {
        clearNavigationStatus();
        setLastActiveUrl(lastActiveUrlFromSettings);
        return;
      }

      const loadedNavigation = await programTreeService.getLastActive();
      clearNavigationStatus();
      if (loadedNavigation && !loadedNavigation.area) {
        loadedNavigation.area = loadedNavigation.areas ? loadedNavigation.areas[0] : null;
      }

      if (loadedNavigation.area == null) {
        logOut();
        return;
      }
      const url = buildNavigationUrl(loadedNavigation);
      //we can't routerNavigate here because that introduces a dependency that changes every time router info changes
      //when that happens, we will actually do the full load from route info twice (once with old, once with new) whenever user navigates to a new program
      //the dual call creates race conditions that are gnarly.
      //in order to keep routerNavigate out of our dependency where we respond to the changes in routeInfo, we have to save the url in a state variable.
      //then respond to that in a separate use effect in order to then use routerNavigate to go there.
      setLastActiveUrl(url);
    } catch (e) {
      setNavigtionError(e);
    }
  },[clearNavigationStatus, programTreeService, setNavigationLoading, setNavigtionError]);
  useEffect(() => {
    const hasValue = stringHasValue(lastActiveUrl);
    const isUndefined = lastActiveUrl === undefined;
    const breakCondition = !hasValue || isUndefined;
    if (breakCondition) {
      return;
    }

    routerNavigate(lastActiveUrl);
    //update routeInfo in the same effect that navigates. Waiting for the location
    //effect to do it leaves a one-commit gap where a freshly loaded user plus the old
    //route info can fire loadLastActive and stomp the url we just navigated to.
    const newRoute = windowLocationToNavigationParameters(new URL(lastActiveUrl, window.location.origin));
    setRouteInfo(current => {
      return routesAreEqual(current,newRoute) ? current : newRoute;
    });
    setLastActiveUrl(undefined);//need to reset it in case the app ever returns to "/"
  },[lastActiveUrl, routerNavigate])

  //whenever route info changes, we need to decide what we do.  Do we know this is a no-navigation route like help center
  //is this a route that requires us to load last active
  //or is this a route that we want to load our navigation and set that navigation for.
  useEffect(() => {
    if (!user) {
      return;
    }
    //a queued navigation means routeInfo is about to change — deciding on the current
    //routeInfo here would race it (e.g. loading last active while the post sign-in url
    //is still on its way to the router, stomping the url the user actually requested).
    if (stringHasValue(lastActiveUrl)) {
      return;
    }

    let {areaId, noNav} = routeInfo ?? {};
    if (noNav) {
      clearNavigationStatus();
      setNavigation({noNav:true});
      return;
    }
    if (areaId) {
      loadNavigationFromRouteInfo(routeInfo)
    } else {
      loadLastActive();
    }
  },
  [clearNavigationStatus, lastActiveUrl, loadLastActive, loadNavigationFromRouteInfo, routeInfo, user]);
  // #endregion

  const onUserAlertDeleted = useCallback((alert) => {
    setAlerts(userAlerts => {
      const match = userAlerts.systemAlerts.find((a) => a.id === alert.id);
      const index = userAlerts.systemAlerts.indexOf(match);
      const newAlerts = { ...userAlerts };
      newAlerts.systemAlerts = [...newAlerts.systemAlerts];
      newAlerts.systemAlerts.splice(index, 1);
      try {
        //fire and forget our call with the alert service to dismiss this alert.
        alertService.dismissSystemAlert(alert);
        // eslint-disable-next-line no-empty
      } catch (e) {}
      return newAlerts;
    })


  },[alertService]);


  //we need to show core loaded when lots of things have finished loading.
  useEffect(() => {
    const loaded = coreStatus?.success ?? false;
    if (user && strings && !loaded) {
      setCoreLoaded();
    }
  },[user, strings, setCoreLoaded, coreStatus.success])

  return {
    coreStatus,
    navigationStatus,
    navigation,
    setNavigationLoading,
    clearNavigationStatus,
    user,
    configuration,
    strings,
    changeLanguage,
    requestLanguageGroups,
    language:languageData.code,
    permissions,
    requestPermissions,
    onImpersonationChange,
    alerts,
    onUserAlertDeleted,
    warned,
    warnedOn,
    warnedOff,
    printOn,
    printMode,
    analyticsPageEvent
  }
}


export default useApp;
